import * as React from "react";
import { cn } from "@/lib/cn";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: React.ReactNode;
  error?: string | null;
}

// Multi-line counterpart to <Input>. Same label / hint / error chrome and
// focus ring, so textareas drop into the same form stacks without the
// per-screen drift that hand-rolled <textarea> elements used to cause.
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    { label, hint, error, className, id, rows = 3, ...rest },
    ref,
  ) {
    const textareaId = id ?? React.useId();
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={textareaId}
            className="text-2xs font-semibold uppercase tracking-wide text-t-3"
          >
            {label}
          </label>
        )}
        <textarea
          id={textareaId}
          ref={ref}
          rows={rows}
          className={cn(
            "w-full resize-y rounded-md border border-line-1 bg-bg-2 px-3 py-2 text-sm leading-relaxed text-t-1 placeholder:text-t-4",
            "transition-colors focus:border-line-3 focus:outline-none focus:ring-4 focus:ring-neon/15",
            "disabled:cursor-not-allowed disabled:opacity-60",
            error && "border-no/60 focus:border-no focus:ring-no/20",
            className,
          )}
          {...rest}
        />
        {(error || hint) && (
          <p className={cn("text-2xs", error ? "text-no" : "text-t-3")}>
            {error ?? hint}
          </p>
        )}
      </div>
    );
  },
);
